/**
 * 프로모션 상세 페이지
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Badge, Button, Spinner } from 'react-bootstrap';
import AdminLayout from '../../components/layout/AdminLayout';
import { promotionService } from '../../services/promotionService';
import { Promotion } from '../../types/promotion';

const PromotionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const promotionId = id ? parseInt(id, 10) : 0;

  const { data: promotion, isLoading } = useQuery({
    queryKey: ['promotion', promotionId],
    queryFn: () => promotionService.getPromotion(promotionId),
    enabled: !!promotionId && promotionId > 0,
  });

  const getStatusBadge = (status: string) => {
    return status === 'active' ? (
      <Badge bg="success">활성</Badge>
    ) : (
      <Badge bg="secondary">비활성</Badge>
    );
  };

  const getDiscountDisplay = (promotion: Promotion) => {
    if (promotion.discount_type === 'percentage') {
      return `${promotion.discount_value}% 할인`;
    } else {
      return `${promotion.discount_value.toLocaleString()}원 할인`;
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!promotion) {
    return (
      <AdminLayout>
        <Card>
          <Card.Body>
            <p>프로모션을 찾을 수 없습니다.</p>
            <Button variant="secondary" onClick={() => navigate('/admin/promotions')}>
              목록으로
            </Button>
          </Card.Body>
        </Card>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>프로모션 상세</h1>
          <div>
            <Button variant="secondary" onClick={() => navigate('/admin/promotions')} className="me-2">
              목록으로
            </Button>
            <Button variant="primary" onClick={() => navigate(`/admin/promotions/${promotionId}/edit`)}>
              수정
            </Button>
          </div>
        </div>

        <Card>
          <Card.Body>
            <div className="mb-3">
              <h3>{promotion.name}</h3>
              <div className="mt-2">
                <Badge bg="info" className="me-2">{promotion.code}</Badge>
                {getStatusBadge(promotion.status)}
              </div>
            </div>

            <div className="mb-3">
              <strong>할인 정보:</strong> {getDiscountDisplay(promotion)}
            </div>

            {promotion.min_purchase_amount && (
              <div className="mb-3">
                <strong>최소 구매 금액:</strong> {promotion.min_purchase_amount.toLocaleString()}원
              </div>
            )}

            {promotion.max_discount_amount && (
              <div className="mb-3">
                <strong>최대 할인 금액:</strong> {promotion.max_discount_amount.toLocaleString()}원
              </div>
            )}

            <div className="mb-3">
              <strong>유효 기간:</strong> {new Date(promotion.valid_from).toLocaleString('ko-KR')} ~ {new Date(promotion.valid_to).toLocaleString('ko-KR')}
            </div>

            <div className="mb-3">
              <strong>사용 현황:</strong> {promotion.usage_count}
              {promotion.usage_limit ? ` / ${promotion.usage_limit}` : ' / 무제한'}
            </div>

            {promotion.applicable_hall_ids && promotion.applicable_hall_ids.length > 0 && (
              <div className="mb-3">
                <strong>적용 가능한 관:</strong> {promotion.applicable_hall_ids.join(', ')}
              </div>
            )}

            <div className="mb-3">
              <strong>등록일:</strong> {new Date(promotion.created_at).toLocaleString('ko-KR')}
            </div>

            {promotion.updated_at && (
              <div className="mb-3">
                <strong>수정일:</strong> {new Date(promotion.updated_at).toLocaleString('ko-KR')}
              </div>
            )}

            {promotion.description && (
              <>
                <hr />
                <div className="mb-3">
                  <h5>설명</h5>
                  <div style={{ 
                    padding: '15px', 
                    backgroundColor: '#2E2E3A', 
                    borderRadius: '5px',
                    color: '#ccc',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {promotion.description}
                  </div>
                </div>
              </>
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default PromotionDetail;

